import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';

import { patchState } from '@res/ng-repository';
import { PartnerFirestoreService } from '../../services/partner.firestore.service';
import { PartnerActions } from './partner.action';
import { PartnerStateModel } from './partner.store';
import { FilterModel } from 'ag-grid-community';

@State<PartnerStateModel>({
  name: 'partner',
  defaults: {
    partners: [],
    isSubscribed: false,
    isLoading: false,
    sort: [],
  },
})
@Injectable()
export class PartnerState {
  private partnerService = inject(PartnerFirestoreService);
  // private userState = inject(UserState);
  // private dbUser = inject(OrmService).userCollection;
  // private authService = inject(Auth);
  // private store = inject(Store);
  @Action(PartnerActions.GetAllPartner)
  async getAllPartner(ctx: StateContext<PartnerStateModel>) {
    ctx.patchState({ isLoading: true });
    const partners = await this.partnerService.GetAll();
    ctx.patchState({
      partners: partners.map(({ data, ref }) => ({ ...data, ref })),
      isLoading: false,
    });
  }

  /**
   * Subscribes once, and keeps the handle that closes it.
   *
   * `onGridReady` dispatches this on every visit to the Partners page, and this handler neither
   * checked `isSubscribed` nor kept the unsubscribe function `OnGetAll` returns. Every visit
   * therefore started another Firestore listener and abandoned the previous one, so a single
   * partner change eventually fired every accumulated callback and was billed as that many reads,
   * until the application was reloaded. The `isSubscribed` field was already on the state model
   * and simply unused; every sibling state in the workspace guards on it the same way.
   */
  @Action(PartnerActions.OnGetAllPartner)
  onGetAllPartner(ctx: StateContext<PartnerStateModel>) {
    const state = ctx.getState();
    if (state.isSubscribed) {
      return;
    }
    ctx.patchState({ isSubscribed: true, isLoading: true });
    const off = this.partnerService.OnGetAll((data) => ctx.dispatch(new PartnerActions.UpdatePartnerResult(data)));
    // Cleared on failure as well as stored, or a listener that never attached would latch the flag
    // and leave the page permanently empty with no way to retry.
    off.catch((subscriptionError) => {
      console.error('Partners listener failed', subscriptionError);
      ctx.patchState({ isSubscribed: false, isLoading: false });
    });
    ctx.patchState({ off });
  }

  @Action(PartnerActions.ClosePartnersConnection)
  closePartnersConnection(ctx: StateContext<PartnerStateModel>) {
    const state = ctx.getState();
    if (state.isSubscribed && state.off) {
      state.off();
    }
    ctx.patchState({ isSubscribed: false, off: undefined });
  }

  @Action(PartnerActions.SavePartner)
  async savePartner(ctx: StateContext<PartnerStateModel>, { partnerData }: PartnerActions.SavePartner) {
    await this.partnerService.Create(partnerData);
    // ctx.dispatch(new PartnerActions.GetAllPartner());
    // ctx.patchState({ isLoading: true });
    // const partners = await this.partnerService.GetAll();
    // ctx.patchState({
    //   partners: partners.map(({ data, ref }) => ({ ...data, ref })),
    //   isLoading: false,
    // });
  }

  @Action(PartnerActions.UpdatePartner)
  async updatePartner(ctx: StateContext<PartnerStateModel>, { partnerData, ref }: PartnerActions.UpdatePartner) {
    await this.partnerService.Update(ref, partnerData);
    ctx.dispatch(new PartnerActions.GetAllPartner());
  }

  @Action(PartnerActions.UpdatePartnerResult)
  updatePartnerResult(ctx: StateContext<PartnerStateModel>, { data }: PartnerActions.UpdatePartnerResult) {
    patchState<PartnerStateModel>(ctx, 'partners', data);
  }

  @Action(PartnerActions.PaginateQueryPartner)
  async queryPartner(
    ctx: StateContext<PartnerStateModel>,
    { request, isResetAfterFilterOrSort }: PartnerActions.PaginateQueryPartner,
  ) {
    const state = ctx.getState();
    // if (request.filterModel && (request.filterModel as FilterModel)['userId']) {
    //   (request.filterModel as FilterModel)['userId']['values'] = (request.filterModel as FilterModel)[
    //     'userId'
    //   ].values.map(
    //     (v: string) => this.store.selectSnapshot(UserState.users).find((user) => user.companyName === v)?.ref
    //   );
    // }
    // if (userId) {
    //   request.filterModel = {
    //     userId: { filter: this.dbUser.ref(this.dbUser.id(userId)), type: 'equals' },
    //     ...request.filterModel,
    //   };
    // }

    ctx.patchState({ isLoading: true });
    const { runs, direction } = this.partnerService.SetQuery({
      ...request,
      count: state.partners.length,
      data: state.partners,
    });
    if (isResetAfterFilterOrSort) {
      const trCound = state.partners.length;
      state.partners = [];
      state.partners.length = trCound;
    }
    if (JSON.stringify(state.filter) !== JSON.stringify(request.filterModel)) {
      ctx.patchState({ filter: request?.filterModel });

      state.partners = [];
      state.partners.length = await this.partnerService.GetBuildedCount(runs);
    }
    if (state.partners.length !== 0) {
      runs.push(...this.partnerService.SetLimit({ ...request, count: state.partners.length, data: [] }));
      ctx.patchState({ sort: request?.sortModel });

      if (!runs.some((x) => x.type === 'limit')) {
        throw new Error('No limit');
      }
      const partners = await this.partnerService.GetBuildedData(runs);
      ctx.patchState({ filter: request?.filterModel });

      if (direction === 'asc') {
        partners.reverse().forEach(
          ({ data, ref }, i) =>
            (state.partners[i + (request?.startRow ?? 0)] = {
              ...data,
              ref,
            }),
        );
      } else {
        partners.forEach(
          ({ data, ref }, i) =>
            (state.partners[i + (request?.startRow ?? 0)] = {
              ...data,
              ref,
            }),
        );
      }
    }

    ctx.patchState({ isLoading: false, partners: state.partners });
  }

  @Action(PartnerActions.PartnerError)
  partnerError(ctx: StateContext<PartnerStateModel>, { error }: PartnerActions.PartnerError) {
    console.error({ error });
    // await this.partnerService.Update(id, partnerData);
  }

  @Action(PartnerActions.DeletePartners)
  async deletetPartner(ctx: StateContext<PartnerStateModel>, { refs }: PartnerActions.DeletePartners) {
    if (Array.isArray(refs)) {
      await this.partnerService.BatchDelete(refs);
    } else {
      await this.partnerService.Delete(refs);
    }
  }

  @Action(PartnerActions.GetSumsCountOf)
  public async getCountOf(
    ctx: StateContext<PartnerStateModel>,
    { fields, timePeriod, filterModel }: PartnerActions.GetSumsCountOf,
  ) {
    const query = {
      filterModel: {},
      startRow: undefined,
      endRow: undefined,
      sortModel: [],
      data: [],
      count: 0,
    };
    if (timePeriod.dateFrom && timePeriod.dateTo) {
      (query.filterModel as FilterModel)['createdAt'] = {
        colId: 'createdAt',
        type: 'inRange',
        filter: timePeriod.dateFrom,
        filterTo: timePeriod.dateTo,
      };
    }
    // if (userId) {
    //   filterModel = { userId: { filter: this.dbUser.ref(this.dbUser.id(userId)), type: 'equals' }, ...filterModel };
    // }
    for (const [fieldKey, fieldFilter] of Object.entries(filterModel ?? {})) {
      (query.filterModel as FilterModel)[fieldKey] = fieldFilter;
    }
    (query.filterModel as FilterModel)['paymentResult'] = {
      colId: 'paymentResult',
      type: 'equals',
      filter: 'Successful',
    };

    // query.filterModel.paymentResult = { colId: 'paymentResult', type: 'equals', filter: 'Successful' };
    const extraQuerry = this.partnerService.SetQuery(query);
    const querrys: { [key: string]: { sum: number; count: number } } = {};
    for (const field of fields) {
      querrys[field.sumId] = {
        sum: await this.partnerService.QuerySumByCreationDate(field.sumId, extraQuerry.runs),
        count: await this.partnerService.QueryCountByCreationDate(field.countId, field.countValue, extraQuerry.runs),
      };
    }
    ctx.patchState({ sums: querrys });
  }

  @Selector()
  static partners(state: PartnerStateModel) {
    return state.partners;
  }
  @Selector()
  static partnersCount(state: PartnerStateModel) {
    return state.partners.length;
  }

  @Selector()
  static sums(state: PartnerStateModel) {
    return state.sums;
  }

  @Selector()
  static sumsByParams(state: PartnerStateModel) {
    return (field: string) => state?.sums?.[field];
  }

  @Selector()
  static partnerState(state: PartnerStateModel) {
    return state;
  }
}
